import { doc, getDoc, collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../firebase-config";

/**
 * Service de calcul des horaires ajustés selon les règles de l'entreprise
 */

/**
 * Obtenir le nom du jour en français
 */
function getDayName(date) {
  const days = ["Dimanche", "Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"];
  return days[date.getDay()];
}

/**
 * Arrondir à l'heure pleine supérieure
 * Ex: 7h50 → 8h00, 8h10 → 9h00
 */
function roundUpToNextHour(date) {
  const rounded = new Date(date);
  if (rounded.getMinutes() > 0 || rounded.getSeconds() > 0 || rounded.getMilliseconds() > 0) {
    rounded.setHours(rounded.getHours() + 1);
  }
  rounded.setMinutes(0, 0, 0);
  return rounded;
}

/**
 * Arrondir à l'heure pleine (inférieure ou égale)
 * Ex: 17h14 → 17h00, 17h00 → 17h00
 */
function roundDownToHour(date) {
  const rounded = new Date(date);
  rounded.setMinutes(0, 0, 0);
  return rounded;
}

/**
 * RÈGLE GÉNÉRALE (sans horaires configurés)
 */
function applyGeneralRules(startTime, endTime) {
  const start = new Date(startTime);
  const end = endTime ? new Date(endTime) : null;
  
  let adjustedStart;
  
  const startMinutes = start.getMinutes();
  
  if (startMinutes === 0) {
    adjustedStart = start;
  } else if (startMinutes <= 31) {
    adjustedStart = start;
  } else {
    adjustedStart = roundUpToNextHour(start);
  }
  
  const adjustedEnd = end;
  
  return {
    adjustedStartTime: adjustedStart,
    adjustedEndTime: adjustedEnd,
    appliedRule: "general"
  };
}

/**
 * RÈGLE AVEC HORAIRES CONFIGURÉS
 */
function applyScheduleRules(startTime, endTime, schedule, toleranceDebut, toleranceFin) {
  const start = new Date(startTime);
  const end = endTime ? new Date(endTime) : null;
  
  if (!schedule || !schedule.debut) {
    return applyGeneralRules(startTime, endTime);
  }
  
  const [heureDebut, minuteDebut] = schedule.debut.split(':').map(Number);
  const expectedStart = new Date(start);
  expectedStart.setHours(heureDebut, minuteDebut, 0, 0);
  
  const diffMinutes = (start - expectedStart) / (1000 * 60);
  
  let adjustedStart;
  
  if (diffMinutes < 0) {
    if (Math.abs(diffMinutes) <= toleranceDebut) {
      adjustedStart = expectedStart;
    } else {
      const generalResult = applyGeneralRules(startTime, endTime);
      adjustedStart = generalResult.adjustedStartTime;
    }
  } else {
    adjustedStart = start;
  }
  
  let adjustedEnd = end;
  
  if (end && schedule.fin) {
    const [heureFin, minuteFin] = schedule.fin.split(':').map(Number);
    const expectedEnd = new Date(end);
    expectedEnd.setHours(heureFin, minuteFin, 0, 0);
    
    const diffMinutesEnd = (end - expectedEnd) / (1000 * 60);
    
    if (diffMinutesEnd > 0 && diffMinutesEnd <= toleranceFin) {
      adjustedEnd = expectedEnd;
    } else {
      adjustedEnd = end;
    }
  }
  
  return {
    adjustedStartTime: adjustedStart,
    adjustedEndTime: adjustedEnd,
    appliedRule: "scheduled",
    expectedStart: expectedStart,
    expectedEnd: schedule.fin ? (() => {
      const [heureFin, minuteFin] = schedule.fin.split(':').map(Number);
      const exp = new Date(end || start);
      exp.setHours(heureFin, minuteFin, 0, 0);
      return exp;
    })() : null
  };
}

/**
 * Calculer les horaires ajustés pour une session
 * @param {Object} session - Session de travail avec startTime et endTime
 * @param {string} employeeEmail - Email de l'employé
 * @returns {Promise<Object>} Horaires ajustés
 */
export async function calculateAdjustedTimes(session, employeeEmail) {
  try {
    console.log('📊 Calcul des horaires ajustés pour:', employeeEmail);
    
    // ✅ FIX: Chercher l'employé par EMAIL au lieu de par UID
    const employeesQuery = query(
      collection(db, "employees"),
      where("email", "==", employeeEmail)
    );
    const employeesSnap = await getDocs(employeesQuery);
    
    if (employeesSnap.empty) {
      console.warn('⚠️ Employé non trouvé, application règle générale');
      return applyGeneralRules(session.startTime, session.endTime);
    }
    
    const employeeData = employeesSnap.docs[0].data();
    
    if (!employeeData.hasWorkingHours) {
      console.log('📋 Pas d\'horaires configurés → Règle générale');
      return applyGeneralRules(session.startTime, session.endTime);
    }
    
    const startDate = session.startTime.toDate ? session.startTime.toDate() : new Date(session.startTime);
    const dayName = getDayName(startDate);
    
    console.log('📅 Jour:', dayName);
    
    const schedule = employeeData.weeklySchedule?.[dayName];
    const toleranceDebut = employeeData.toleranceDebut || 0;
    const toleranceFin = employeeData.toleranceFin || 0;
    
    console.log('⏰ Horaires prévus:', schedule);
    console.log('⚖️ Tolérances:', { debut: toleranceDebut, fin: toleranceFin });
    
    const endTime = session.endTime?.toDate ? session.endTime.toDate() : (session.endTime ? new Date(session.endTime) : null);
    
    return applyScheduleRules(
      startDate,
      endTime,
      schedule,
      toleranceDebut,
      toleranceFin
    );
    
  } catch (error) {
    console.error('❌ Erreur calcul horaires ajustés:', error);
    return applyGeneralRules(session.startTime, session.endTime);
  }
}

/**
 * Calculer le temps de travail ajusté en millisecondes
 */
export function calculateAdjustedWorkTime(adjustedStart, adjustedEnd, totalPauseTime = 0) {
  if (!adjustedStart || !adjustedEnd) return 0;
  
  const totalTime = adjustedEnd - adjustedStart;
  const workTime = totalTime - totalPauseTime;
  
  return Math.max(0, workTime);
}

export default {
  calculateAdjustedTimes,
  calculateAdjustedWorkTime
};